import React, { useEffect, useState } from "react";
import { useNotification } from "../../../../NotificationContext";
import LoaderComponent from "../../../../components/LoaderComponent";
import { format } from "date-fns";
import { getIPDVisitBilling, getVisitTransactions, createIPDInvoice, getVisit, getVisitInsuranceClaimsList, saveVisitInsuranceClaim, updateVisitInsuranceClaim, getVisitEntriesTimeline, applyVisitAdvance, updateRoomEntryBilledRate } from "../../../../apis/visit";
import { getInsuranceProviders, getClaimTemplates } from "../../../../apis/insuranceProviders";
import { processPayment } from "../../../../apis/payment";
import { savePatientTransaction } from "../../../../apis/patientTransaction";
import PaymentModal, { PaymentData } from "../../../../components/PaymentModal";
import { getAllocationReceipt } from "../../../../apis/completedPayments";
import ReceiptSlipModal, { ReceiptSlipData } from "../../../../components/receipts/ReceiptSlipModal";

interface IPDBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitId: number;
  patientId: number;
}

interface BillingData {
  pharmacy: Array<{
    id: number;
    saleNumber: string;
    saleDate: string;
    items: Array<{
      name: string;
      quantity: number;
      sellingPrice: number;
      discountPercentage: number;
      taxPercentage: number;
      total: number;
    }>;
    totalAmount: number;
    discountAmount: number;
    taxAmount: number;
    netAmount: number;
  }>;
  labOrders: Array<{
    id: number;
    orderNumber: string;
    orderDate: string;
    items: Array<{
      name: string;
      fee: number;
    }>;
    totalAmount: number;
  }>;
  services: Array<{
    id: number;
    name: string;
    fee: number;
    quantity: number;
    total: number;
    categoryCode?: string | null;
  }>;
  roomCharges?: Array<{
    roomId: number;
    roomName: string;
    roomType: string;
    roomTypePricePerDay: number;
    billedRatePerDay: number | null;
    hasMismatch: boolean;
    fromDate: string;
    toDate: string;
    durationDays: number;
    durationHours: number;
    charge: number;
    visitEntryId: number | null;
  }>;
  patientBalance?: number;
}

type NavigationItem = "summary" | "billItems" | "payments" | "insurance";

interface ClaimBreakdownLine {
  serviceCategoryCode: string;
  providerLabel: string;
  amount: string;
}

interface InsuranceClaimForm {
  insuranceProviderId: number | null;
  policyNumber: string;
  coverageType: "reimbursement" | "cashless";
  claimedAmount: string;
  approvedAmount: string;
  rejectedAmount: string;
  status: string;
  claimDate: string;
  approvalDate: string;
  remarks: string;
}

const IPDBillingModal: React.FC<IPDBillingModalProps> = ({
  isOpen,
  onClose,
  visitId,
  patientId,
}) => {
  const { showNotification } = useNotification();
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeNav, setActiveNav] = useState<NavigationItem>("summary");
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    pharmacy: false,
    labOrders: false,
    services: false,
    roomCharges: false,
  });
  const [visitTransactions, setVisitTransactions] = useState<any[]>([]);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
  const [rateOverrides, setRateOverrides] = useState<Record<string, number>>({});
  const [excludedRooms, setExcludedRooms] = useState<Set<string>>(new Set());
  const [ratesSaved, setRatesSaved] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [advanceBalance, setAdvanceBalance] = useState<number | null>(null);
  const [customItems, setCustomItems] = useState<
    Array<{ id: string; name: string; quantity: number; rate: number }>
  >([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentContext, setPaymentContext] = useState<"selected" | "outstanding">("outstanding");
  const [isReceiptSlipOpen, setIsReceiptSlipOpen] = useState(false);
  const [receiptSlipData, setReceiptSlipData] = useState<ReceiptSlipData | null>(null);
  const [visit, setVisit] = useState<any>(null);
  const [insuranceProviders, setInsuranceProviders] = useState<Array<{ id: number; name: string }>>([]);
  const [insuranceForm, setInsuranceForm] = useState<InsuranceClaimForm>({
    insuranceProviderId: null,
    policyNumber: "",
    coverageType: "reimbursement",
    claimedAmount: "",
    approvedAmount: "",
    rejectedAmount: "",
    status: "",
    claimDate: "",
    approvalDate: "",
    remarks: "",
  });
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);
  const [isLoadingInsurance, setIsLoadingInsurance] = useState(false);
  const [insuranceViewMode, setInsuranceViewMode] = useState<"list" | "form">("list");
  const [insuranceClaimsList, setInsuranceClaimsList] = useState<any[]>([]);
  const [editingClaimId, setEditingClaimId] = useState<number | null>(null);
  const [defaultInsuranceFormFromAdmission, setDefaultInsuranceFormFromAdmission] = useState<InsuranceClaimForm | null>(null);
  const [claimTemplates, setClaimTemplates] = useState<any[]>([]);
  const [claimBreakdown, setClaimBreakdown] = useState<ClaimBreakdownLine[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const hasInvoice = Boolean(invoiceData?.invoice);

  const buildRateOverrides = (data: BillingData) => {
    const overrides: Record<string, number> = {};
    data.pharmacy.forEach((sale) => {
      overrides[`pharmacy-${sale.id}`] = sale.netAmount || 0;
    });
    data.labOrders.forEach((order) => {
      overrides[`lab-${order.id}`] = order.totalAmount || 0;
    });
    data.services.forEach((service) => {
      overrides[`service-${service.id}`] = service.total || 0;
    });
    data.roomCharges?.forEach((room, idx) => {
      overrides[`room-${room.roomId}-${idx}`] = room.charge || 0;
    });
    return overrides;
  };

  const addCustomItem = () => {
    setCustomItems((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: "",
        quantity: 1,
        rate: 0,
      },
    ]);
  };

  const updateCustomItem = (
    id: string,
    updates: Partial<{ name: string; quantity: number; rate: number }>
  ) => {
    setRatesSaved(false);
    setCustomItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const removeCustomItem = (id: string) => {
    setCustomItems((prev) => prev.filter((item) => item.id !== id));
  };

  useEffect(() => {
    if (isOpen && visitId) {
      fetchBillingData();
      if (activeNav === "payments") {
        fetchPaymentData();
      }
      if (activeNav === "insurance") {
        fetchInsuranceData();
      }
    }
  }, [isOpen, visitId, activeNav]);

  const ratesStorageKey = `ipd_billing_rates_${visitId}`;

  const persistRates = (overrides: Record<string, number>, items: typeof customItems, excluded: Set<string> = excludedRooms) => {
    localStorage.setItem(ratesStorageKey, JSON.stringify({ overrides, customItems: items, excludedRooms: Array.from(excluded) }));
  };

  const clearPersistedRates = () => {
    localStorage.removeItem(ratesStorageKey);
  };

  const handleSaveRates = async () => {
    persistRates(rateOverrides, customItems, excludedRooms);

    // Persist room rate overrides to the backend so they survive page reloads
    if (billingData?.roomCharges) {
      const roomSavePromises = billingData.roomCharges
        .map((room, idx) => {
          const key = `room-${room.roomId}-${idx}`;
          const overrideValue = rateOverrides[key];
          if (room.visitEntryId != null && overrideValue !== undefined && overrideValue !== room.charge) {
            return updateRoomEntryBilledRate(visitId, room.visitEntryId, overrideValue).catch(() => {
              // Non-blocking: localStorage still has the override
            });
          }
          return null;
        })
        .filter(Boolean);
      await Promise.all(roomSavePromises);
    }

    setRatesSaved(true);
  };

  useEffect(() => {
    if (billingData && !hasInvoice) {
      const stored = localStorage.getItem(ratesStorageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed.overrides) setRateOverrides(parsed.overrides);
          if (parsed.customItems) setCustomItems(parsed.customItems);
          if (parsed.excludedRooms) setExcludedRooms(new Set(parsed.excludedRooms));
          setRatesSaved(true);
          return;
        } catch {
          // fall through to defaults
        }
      }
      setRateOverrides(buildRateOverrides(billingData));
    }
  }, [billingData, hasInvoice]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBillingData = async () => {
    setIsLoading(true);
    try {
      const response = await getIPDVisitBilling(visitId);
      setBillingData(response as BillingData);
    } catch (error: any) {
      console.error("Failed to fetch billing data:", error);
      showNotification(
        "error",
        "Error",
        error?.response?.data?.message || "Failed to load billing data",
        "topRight"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentData = async () => {
    try {
      // Fetch invoice data (includes visit-level transactions and allocations)
      const invoiceRes: any = await getVisitTransactions(visitId);
      const invoicePayload = invoiceRes?.data || null;
      setInvoiceData(invoicePayload);
      setVisitTransactions(invoicePayload?.invoice?.transactions || []);
      const balanceFromInvoice =
        invoicePayload?.patientBalance ??
        0;
      setAdvanceBalance(balanceFromInvoice);

    } catch (error: any) {
      console.error("Failed to fetch payment data:", error);
    }
  };

  const fetchInsuranceData = async () => {
    setIsLoadingInsurance(true);
    try {
      // Fetch visit (has meta from admission), timeline (admission entry form_data), and providers.
      // Do NOT require insurance-claim API on load so billing works even before backend adds that route.
      const [visitRes, timelineRes, providersRes]: any[] = await Promise.all([
        getVisit(visitId),
        getVisitEntriesTimeline(visitId).catch(() => ({ data: [] })),
        getInsuranceProviders({ per_page: 1000 }),
      ]);
      const visitData = visitRes?.data ?? visitRes;
      setVisit(visitData);
      const providersList = providersRes?.data?.data ?? providersRes?.data ?? providersRes ?? [];
      setInsuranceProviders(Array.isArray(providersList) ? providersList : []);

      // Fetch all insurance claims for this visit (list; backend may return array or single)
      let claimsList: any[] = [];
      try {
        claimsList = await getVisitInsuranceClaimsList(visitId);
      } catch {
        // Route may not exist yet
      }
      setInsuranceClaimsList(Array.isArray(claimsList) ? claimsList : []);

      const meta = visitData?.meta ?? {};
      const entries = timelineRes?.data ?? timelineRes?.entries ?? timelineRes ?? [];
      const admissionEntry = Array.isArray(entries)
        ? entries.find((e: any) => (e.entryType || e.entry_type || "").toLowerCase().includes("admission"))
        : null;
      const admissionData = admissionEntry?.formData ?? admissionEntry?.form_data ?? admissionEntry?.data ?? {};

      // Admission insurance: from visit.meta first, then from admission entry form_data
      const fromMeta = {
        insuranceProviderId: meta.insuranceProviderId ?? meta.insurance_provider_id ?? null,
        policyNumber: meta.policyNumber ?? meta.policy_number ?? "",
        coverageType: (meta.coverageType ?? meta.coverage_type ?? "reimbursement") as "reimbursement" | "cashless",
        providerName:
          meta.insuranceProvider ??
          meta.insuranceProviderName ??
          meta.insurance_provider ??
          meta.insurance_provider_name ??
          "",
      };
      const fromAdmission = {
        insuranceProviderId: admissionData.insuranceProviderId ?? admissionData.insurance_provider_id ?? null,
        policyNumber: admissionData.policyNumber ?? admissionData.policy_number ?? "",
        coverageType: (admissionData.coverageType ?? admissionData.coverage_type ?? "reimbursement") as "reimbursement" | "cashless",
        providerName:
          admissionData.insuranceProvider?.name ??
          admissionData.insuranceProviderName ??
          admissionData.insurance_provider?.name ??
          admissionData.insurance_provider_name ??
          (typeof admissionData.insuranceProvider === "string" ? admissionData.insuranceProvider : "") ??
          "",
      };

      // Build default form from admission only (for "Add insurance claim")
      let resolvedProviderId =
        fromMeta.insuranceProviderId ?? fromAdmission.insuranceProviderId ?? null;
      const providerNameSource =
        (fromMeta.providerName || fromAdmission.providerName || "").trim();
      if (!resolvedProviderId && providerNameSource && Array.isArray(providersList)) {
        const match = providersList.find((p: any) =>
          String(p.name || "").trim().toLowerCase() === providerNameSource.toLowerCase()
        );
        if (match) resolvedProviderId = match.id;
      }
      const policyNumber =
        (fromMeta.policyNumber || fromAdmission.policyNumber || "").trim();
      const coverageType =
        (fromMeta.coverageType ?? fromAdmission.coverageType) || "reimbursement";
      setDefaultInsuranceFormFromAdmission({
        insuranceProviderId: resolvedProviderId ?? null,
        policyNumber,
        coverageType: coverageType as "reimbursement" | "cashless",
        claimedAmount: "",
        approvedAmount: "",
        rejectedAmount: "",
        status: "",
        claimDate: "",
        approvalDate: "",
        remarks: "",
      });
    } catch (error: any) {
      console.error("Failed to fetch insurance data:", error);
      showNotification("error", "Error", error?.response?.data?.message || "Failed to load insurance data", "topRight");
    } finally {
      setIsLoadingInsurance(false);
    }
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return "₹0.00";
    }
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) {
      return "₹0.00";
    }
    return `₹${num.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "dd MMM yyyy hh:mm a");
    } catch (error) {
      return "-";
    }
  };

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "dd MMM yyyy, HH:mm");
    } catch (error) {
      return "-";
    }
  };

  const calculatePharmacyTotal = () => {
    if (!billingData) return 0;
    return billingData.pharmacy.reduce(
      (sum, sale) => sum + getRateValue(`pharmacy-${sale.id}`, sale.netAmount || 0),
      0
    );
  };

  const calculateLabTotal = () => {
    if (!billingData) return 0;
    return billingData.labOrders.reduce((sum, order) => {
      const defaultAmount =
        order.totalAmount && order.totalAmount > 0
          ? order.totalAmount
          : order.items?.reduce((s, item) => s + (item.fee || 0), 0) || 0;
      return sum + getRateValue(`lab-${order.id}`, defaultAmount);
    }, 0);
  };

  const calculateServicesTotal = () => {
    if (!billingData) return 0;
    return billingData.services.reduce(
      (sum, service) => sum + getRateValue(`service-${service.id}`, service.total || 0),
      0
    );
  };

  const calculateRoomChargesTotal = () => {
    if (!billingData || !billingData.roomCharges) return 0;
    return billingData.roomCharges.reduce((sum, room, idx) => {
      const key = `room-${room.roomId}-${idx}`;
      if (excludedRooms.has(key)) return sum;
      return sum + getRateValue(key, room.charge || 0);
    }, 0);
  };

  const calculateCustomItemsTotal = () =>
    customItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.rate || 0), 0);

  const calculateGrandTotal = () =>
    calculatePharmacyTotal() +
    calculateLabTotal() +
    calculateServicesTotal() +
    calculateRoomChargesTotal() +
    calculateCustomItemsTotal();

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const calculateOutstanding = () => {
    const grandTotal = calculateGrandTotal();
    const totalAllocated = invoiceData?.invoice?.paidAmount || 0;
    return Math.max(0, grandTotal - totalAllocated);
  };

  const calculateUnallocatedBalance = () => {
    const totalTransactions = visitTransactions.reduce(
      (sum, txn) => sum + (parseFloat(txn.amount) || 0),
      0
    );
    console.log({ totalTransactions });
    const totalAllocated = invoiceData?.invoice?.paymentAllocations?.reduce(
      (sum: number, alloc: any) => sum + (parseFloat(alloc.allocatedAmount) || 0),
      0
    ) || 0;
    return Math.max(0, totalTransactions - totalAllocated);
  };

  const getAdvanceBalance = () => {
    console.log({ advanceBalance });
    if (advanceBalance !== null && advanceBalance !== undefined) {
      return advanceBalance;
    }
    console.log({ billingData });
    if (billingData?.patientBalance !== undefined && billingData?.patientBalance !== null) {
      return billingData.patientBalance;
    }
    return calculateUnallocatedBalance();
  };

  const getRateValue = (key: string, fallback: number) => {
    const value = rateOverrides[key];
    if (value === undefined || value === null || Number.isNaN(value)) {
      return fallback;
    }
    return value;
  };

  const calculateEditableTotal = () => {
    if (!billingData) return 0;
    let total = 0;
    billingData.pharmacy.forEach((sale) => {
      total += getRateValue(`pharmacy-${sale.id}`, sale.netAmount || 0);
    });
    billingData.labOrders.forEach((order) => {
      total += getRateValue(`lab-${order.id}`, order.totalAmount || 0);
    });
    billingData.services.forEach((service) => {
      total += getRateValue(`service-${service.id}`, service.total || 0);
    });
    billingData.roomCharges?.forEach((room, idx) => {
      total += getRateValue(`room-${room.roomId}-${idx}`, room.charge || 0);
    });
    customItems.forEach((item) => {
      total += (item.quantity || 0) * (item.rate || 0);
    });
    return total;
  };

  const handleAdvanceSave = async (paymentData: PaymentData) => {
    if (!patientId) return;

    try {
      const paymentModes = [
        { mode: "cash", amount: paymentData.cash },
        { mode: "upi", amount: paymentData.upi },
        { mode: "card", amount: paymentData.card },
        { mode: "cheque", amount: paymentData.cheque },
        { mode: "online", amount: paymentData.online },
      ];

      const savePromises = paymentModes
        .filter((p) => p.amount > 0)
        .map((payment) =>
          savePatientTransaction({
            patientId,
            amount: payment.amount,
            paymentMode: payment.mode,
            note: paymentData.paymentNote || "",
            date: format(new Date(), "yyyy-MM-dd HH:mm").toString(),
          })
        );

      await Promise.all(savePromises);
      showNotification("success", "Advance collected successfully", "", "topRight");
      setIsAdvanceModalOpen(false);
      fetchPaymentData();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to collect advance",
        err?.message || "",
        "topRight"
      );
    }
  };

  const buildInvoiceItems = () => {
    if (!billingData) return [];
    const items: Array<{
      itemType: string;
      refId: number;
      quantity: number;
      description: string;
      unitPrice: number;
      amount: number;
      name?: string;
    }> = [];

    billingData.pharmacy.forEach((sale) => {
      const amount = getRateValue(`pharmacy-${sale.id}`, sale.netAmount || 0);
      items.push({
        itemType: "pharmacy",
        refId: sale.id,
        quantity: 1,
        description: sale.items.map((item) => item.name).join(", "),
        unitPrice: amount,
        amount,
      });
    });

    billingData.labOrders.forEach((order) => {
      const amount = getRateValue(`lab-${order.id}`, order.totalAmount || 0);
      items.push({
        itemType: "lab",
        refId: order.id,
        quantity: 1,
        description: `Lab Order #${order.orderNumber} - ${order.orderDate}`,
        unitPrice: amount,
        amount,
      });
    });

    billingData.services.forEach((service) => {
      const amount = getRateValue(`service-${service.id}`, service.total || 0);
      const quantity = service.quantity || 1;
      items.push({
        itemType: "service",
        refId: service.id,
        quantity,
        description: `Service #${service.id} - ${service.name}`,
        unitPrice: quantity > 0 ? amount / quantity : amount,
        amount,
      });
    });

    billingData.roomCharges?.forEach((room, idx) => {
      const key = `room-${room.roomId}-${idx}`;
      if (excludedRooms.has(key)) return;
      const amount = getRateValue(key, room.charge || 0);
      items.push({
        itemType: "room",
        description: `Room ${room.roomName} (${room.roomType})`,
        refId: room.roomId,
        quantity: 1,
        unitPrice: amount,
        amount,
      });
    });

    customItems
      .filter((item) => item.name.trim() && item.quantity > 0)
      .forEach((item) => {
        const quantity = item.quantity || 1;
        const rate = item.rate || 0;
        items.push({
          itemType: "custom",
          description: item.name.trim(),
          refId: 0,
          name: item.name.trim(),
          quantity,
          unitPrice: rate,
          amount: quantity * rate,
        });
      });

    return items;
  };

  const handleGenerateInvoice = async () => {
    if (!billingData || isGeneratingInvoice) return;
    const items = buildInvoiceItems();
    if (items.length === 0) {
      showNotification("warning", "No bill items available", "", "topRight");
      return;
    }
    setIsGeneratingInvoice(true);
    try {
      await createIPDInvoice(visitId, { items });
      clearPersistedRates();
      setRatesSaved(false);
      showNotification("success", "Bill generated successfully", "", "topRight");
      fetchPaymentData();
      fetchBillingData();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to generate bill",
        err?.response?.data?.message || err?.message || "",
        "topRight"
      );
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleItemSelection = (itemKey: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  const getSelectedItemsTotal = () => {
    if (!billingData) return 0;
    let total = 0;

    selectedItems.forEach((key) => {
      const [type, id] = key.split("-");
      if (type === "pharmacy") {
        const sale = billingData.pharmacy.find((s) => s.id === parseInt(id));
        if (sale) total += sale.netAmount || 0;
      } else if (type === "lab") {
        const order = billingData.labOrders.find((o) => o.id === parseInt(id));
        if (order) total += order.totalAmount || 0;
      } else if (type === "service") {
        const service = billingData.services.find((s) => s.id === parseInt(id));
        if (service) total += service.total || 0;
      } else if (type === "room") {
        const room = billingData.roomCharges?.find((r) => r.roomId === parseInt(id));
        if (room) total += room.charge || 0;
      }
    });

    return total;
  };

  const buildPaymentSourcesFromSelection = () => {
    const sourceIds: number[] = [];
    const sourceAmounts: Record<number, number> = {};

    selectedItems.forEach((key) => {
      const [type, id] = key.split("-");
      const numId = parseInt(id);

      if (type === "pharmacy") {
        sourceIds.push(numId);
        const sale = billingData?.pharmacy.find((s) => s.id === numId);
        if (sale) sourceAmounts[numId] = sale.netAmount || 0;
      } else if (type === "lab") {
        sourceIds.push(numId);
        const order = billingData?.labOrders.find((o) => o.id === numId);
        if (order) sourceAmounts[numId] = order.totalAmount || 0;
      }
    });

    const hasServicesOrRooms = Array.from(selectedItems).some(
      (key) => key.startsWith("service-") || key.startsWith("room-")
    );

    if (hasServicesOrRooms) {
      sourceIds.push(visitId);
    }

    return {
      sourceIds: sourceIds.length > 0 ? sourceIds : [visitId],
      sourceAmounts,
    };
  };

  const handleProcessPayment = async () => {
    if (selectedItems.size === 0) {
      showNotification("warning", "Please select items to pay", "", "topRight");
      return;
    }

    setIsProcessingPayment(true);
    try {
      const { sourceIds, sourceAmounts } = buildPaymentSourcesFromSelection();

      const payload: any = {
        sourceType: "visit",
        sourceIds,
        payments: [
          {
            amount: getSelectedItemsTotal(),
            paymentMode: "cash",
            paymentDate: new Date().toISOString().split("T")[0],
            allocationType: "manual",
          },
        ],
        sourceAmounts: sourceAmounts,
      };

      await processPayment(payload);
      showNotification("success", "Payment processed successfully", "", "topRight");
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      console.error("Failed to process payment:", error);
      showNotification(
        "error",
        "Failed to process payment",
        error?.response?.data?.message || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const openPaymentModal = (context: "selected" | "outstanding") => {
    if (!hasInvoice) return;
    const outstanding = calculateOutstanding();
    if (outstanding <= 0) {
      showNotification("info", "Invoice already paid", "", "topRight");
      return;
    }
    if (context === "selected" && selectedItems.size === 0) {
      showNotification("warning", "Please select items to pay", "", "topRight");
      return;
    }
    setPaymentContext(context);
    setIsPaymentModalOpen(true);
  };

  const handleInvoicePaymentSave = async (paymentData: PaymentData) => {
    if (!hasInvoice) return;
    const selectedTotal = getSelectedItemsTotal();
    const outstanding = calculateOutstanding();
    const totalDue = paymentContext === "selected" ? selectedTotal : outstanding;
    const maxPayable = Math.min(totalDue, outstanding);
    if (maxPayable <= 0) {
      showNotification("warning", "Nothing due for payment", "", "topRight");
      return;
    }

    const paymentModes = [
      { mode: "cash", amount: paymentData.cash },
      { mode: "upi", amount: paymentData.upi },
      { mode: "card", amount: paymentData.card },
      { mode: "cheque", amount: paymentData.cheque },
      { mode: "online", amount: paymentData.online },
      { mode: "insurance", amount: paymentData.insuranceUsed || 0 },
    ];
    const payments: Array<{
      amount: number;
      paymentMode: string;
      paymentDate: string;
      transactionId?: string;
      notes?: string;
      allocationType?: "manual" | "auto";
      allocationNotes?: string;
    }> = [];
    const currentDate = new Date().toISOString().split("T")[0];
    paymentModes
      .filter((p) => p.amount > 0)
      .forEach((payment) => {
        payments.push({
          amount: payment.amount,
          paymentMode: payment.mode,
          paymentDate: currentDate,
          transactionId: payment.mode !== "cash" && payment.mode !== "insurance"
            ? paymentData.transactionRef || undefined
            : undefined,
          notes: paymentData.paymentNote || undefined,
          allocationType: "manual",
        });
      });

    const advanceUsed = paymentData.advanceUsed || 0;

    if (payments.length === 0 && advanceUsed <= 0) {
      showNotification("warning", "Please enter a payment amount", "", "topRight");
      return;
    }

    const totalPayment = payments.reduce(
      (sum, payment) => sum + (parseFloat(String(payment.amount)) || 0),
      0
    );
    if (totalPayment > maxPayable) {
      showNotification(
        "warning",
        "Payment exceeds amount due",
        `Max allowed: ${formatCurrency(maxPayable)}`,
        "topRight"
      );
      return;
    }

    setIsProcessingPayment(true);
    try {
      // Always route IPD payments against the visit invoice directly.
      // Item selection is only for amount calculation — using item-level IDs
      // as sourceIds with sourceType:"visit" causes the backend to look up the
      // wrong visit (Visit::find(pharmacySaleId)) and allocates to the wrong invoice.
      const payload: any = {
        sourceType: "visit",
        sourceIds: [visitId],
        payments,
      };

      if (payments.length > 0) {
        await processPayment(payload);
      }
      if (advanceUsed > 0) {
        await applyVisitAdvance(visitId, advanceUsed);
      }
      showNotification("success", "Payment processed successfully", "", "topRight");
      setIsPaymentModalOpen(false);
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      showNotification(
        "error",
        "Failed to process payment",
        error?.response?.data?.error?.details || error?.response?.data?.error || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleUseAdvance = async () => {
    if (!hasInvoice) return;
    const balance = getAdvanceBalance();
    const outstanding = calculateOutstanding();
    const selectedTotal = getSelectedItemsTotal();
    const targetAmount = selectedItems.size > 0 ? selectedTotal : outstanding;
    const amountToApply = Math.min(balance, targetAmount);

    if (amountToApply <= 0) {
      showNotification("warning", "No advance available to allocate", "", "topRight");
      return;
    }

    setIsProcessingPayment(true);
    try {
      await applyVisitAdvance(visitId, amountToApply);
      showNotification("success", "Advance applied successfully", "", "topRight");
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      showNotification(
        "error",
        "Failed to apply advance",
        error?.response?.data?.error || error?.response?.data?.message || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePrintReceipt = async (allocationId: number) => {
    try {
      const res: any = await getAllocationReceipt(allocationId);
      const d = res?.data;
      if (!d) return;
      setReceiptSlipData({
        patientName: d.patientName ?? "",
        receiptNumber: d.receiptNumber ?? "",
        invoiceNumber: d.invoiceNumber ?? "",
        paymentDate: d.paymentDate ?? null,
        paymentMode: d.paymentMode ?? "Cash",
        amount: d.amount ?? 0,
        clinicName: d.clinicName ?? undefined,
        notes: d.notes ?? null,
      });
      setIsReceiptSlipOpen(true);
    } catch {
      showNotification("error", "Failed to load receipt", "", "topRight");
    }
  };

  const renderSummaryView = () => {
    if (!billingData) return null;

    return (
      <div className="space-y-4">
        {/* Pharmacy Section */}
        {billingData.pharmacy.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("pharmacy")}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Pharmacy Orders
              </h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculatePharmacyTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.pharmacy ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.pharmacy && (
              <div className="p-4 space-y-4">
                {billingData.pharmacy.map((sale) => (
                  <div key={sale.id} className="pb-4 border-b last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <span className="font-medium text-gray-700">
                          Sale #{sale.saleNumber}
                        </span>
                        <span className="ml-2 text-sm text-gray-500">
                          {formatDate(sale.saleDate)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(sale.netAmount)}
                      </span>
                    </div>
                    <div className="mt-2 ml-4 space-y-1">
                      {sale.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-sm text-gray-600"
                        >
                          <span>
                            {item.name} × {item.quantity}
                          </span>
                          <span>{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lab Orders Section */}
        {billingData.labOrders.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("labOrders")}
            >
              <h3 className="text-lg font-semibold text-gray-900">Lab Orders</h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateLabTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.labOrders ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.labOrders && (
              <div className="p-4 space-y-4">
                {billingData.labOrders.map((order) => (
                  <div key={order.id} className="pb-4 border-b last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <span className="font-medium text-gray-700">
                          Order #{order.orderNumber}
                        </span>
                        <span className="ml-2 text-sm text-gray-500">
                          {formatDate(order.orderDate)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(order.totalAmount)}
                      </span>
                    </div>
                    <div className="mt-2 ml-4 space-y-1">
                      {order.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-sm text-gray-600"
                        >
                          <span>{item.name}</span>
                          <span>{formatCurrency(item.fee)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Services Section */}
        {billingData.services.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("services")}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Visit Services
              </h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateServicesTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.services ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.services && (
              <div className="p-4">
                <div className="space-y-2">
                  {billingData.services.map((service) => (
                    <div
                      key={service.id}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-gray-700">
                        {service.name} {service.quantity > 1 && `× ${service.quantity}`}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(service.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Room Charges Section */}
        {billingData.roomCharges && billingData.roomCharges.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("roomCharges")}
            >
              <h3 className="text-lg font-semibold text-gray-900">Room Charges</h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateRoomChargesTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.roomCharges ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.roomCharges && (
              <div className="p-4 space-y-4">
                {billingData.roomCharges.map((room, idx) => {
                  const key = `room-${room.roomId}-${idx}`;
                  const isExcluded = excludedRooms.has(key);
                  const durationDays = room.durationDays ?? 0;
                  const durationHours = room.durationHours ?? 0;
                  const isValidDuration =
                    !isNaN(durationDays) &&
                    !isNaN(durationHours) &&
                    durationDays >= 0 &&
                    durationHours >= 0;

                  return (
                    <div
                      key={`room-${room.roomId}-${idx}-${room.fromDate}`}
                      className={`pb-4 border-b last:border-b-0 last:pb-0 ${isExcluded ? "opacity-50" : ""} ${room.hasMismatch ? "bg-amber-50 rounded-lg p-3" : ""}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-700">
                              {room.roomName} ({room.roomType})
                            </span>
                            {isExcluded && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 border border-gray-300">
                                Not billed
                              </span>
                            )}
                            {!isExcluded && room.hasMismatch && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-300">
                                ⚠ Rate override
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            {formatDate(room.fromDate)} – {formatDate(room.toDate)}
                          </div>
                          {isValidDuration && (
                            <div className="mt-0.5 text-xs text-gray-400">
                              {Math.ceil(durationDays)} day(s)
                            </div>
                          )}
                          {room.hasMismatch && (
                            <div className="mt-1 text-xs text-amber-600">
                              Billed at ₹{room.billedRatePerDay}/day · Room default: ₹{room.roomTypePricePerDay}/day
                            </div>
                          )}
                          {!room.hasMismatch && room.billedRatePerDay !== null && (
                            <div className="mt-0.5 text-xs text-gray-400">
                              ₹{room.billedRatePerDay}/day
                            </div>
                          )}
                        </div>
                        <span className="font-semibold text-gray-900 ml-4 flex-shrink-0">
                          {formatCurrency(room.charge)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Grand Total */}
        <div className="bg-gray-50 rounded-lg border-2">
          <div className="p-4">
            <div className="flex justify-between items-center">
              <span className="text-xl font-bold text-gray-900">Grand Total:</span>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(calculateGrandTotal())}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBillItemsView = () => {
    if (!billingData) return null;

    return (
      <div className="p-6 space-y-6 bg-white" style={{ minHeight: "800px" }}>
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Provisional Bill</h2>
          <p className="mt-2 text-gray-600">IPD Visit - {formatDate(new Date().toISOString())}</p>
        </div>

        {/* Pharmacy */}
        {billingData.pharmacy.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Pharmacy Orders</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Sale #</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Items</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.pharmacy.map((sale) => (
                  <tr key={sale.id} className="border-b">
                    <td className="p-2">{sale.saleNumber}</td>
                    <td className="p-2">{formatDate(sale.saleDate)}</td>
                    <td className="p-2">
                      {sale.items.map((item, idx) => (
                        <div key={idx} className="text-sm">
                          {item.name} × {item.quantity}
                        </div>
                      ))}
                    </td>
                    <td className="p-2 text-right">{formatCurrency(getRateValue(`pharmacy-${sale.id}`, sale.netAmount))}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculatePharmacyTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Lab Orders */}
        {billingData.labOrders.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Lab Orders</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Order #</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Test</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.labOrders.map((order) => (
                  <tr key={order.id} className="border-b">
                    <td className="p-2">{order.orderNumber}</td>
                    <td className="p-2">{formatDate(order.orderDate)}</td>
                    <td className="p-2">
                      {order.items.map((item, idx) => (
                        <div key={idx}>{item.name}</div>
                      ))}
                    </td>
                    <td className="p-2 text-right">{formatCurrency(getRateValue(`lab-${order.id}`, order.totalAmount))}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateLabTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Services */}
        {billingData.services.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Visit Services</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Service</th>
                  <th className="p-2 text-left">Quantity</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.services.map((service) => (
                  <tr key={service.id} className="border-b">
                    <td className="p-2">{service.name}</td>
                    <td className="p-2">{service.quantity}</td>
                    <td className="p-2 text-right">{formatCurrency(getRateValue(`service-${service.id}`, service.total))}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={2} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateServicesTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Room Charges */}
        {billingData.roomCharges && billingData.roomCharges.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Room Charges</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Room</th>
                  <th className="p-2 text-left">Period</th>
                  <th className="p-2 text-left">Duration</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.roomCharges.map((room, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="p-2">
                      {room.roomName} ({room.roomType})
                    </td>
                    <td className="p-2 text-sm">
                      {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                    </td>
                    <td className="p-2 text-sm">
                      {Math.ceil(room.durationDays)} day(s)
                    </td>
                    <td className="p-2 text-right">{formatCurrency(getRateValue(`room-${room.roomId}-${idx}`, room.charge))}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateRoomChargesTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Custom Items */}
        {customItems.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Additional Charges</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-left">Qty</th>
                  <th className="p-2 text-right">Rate</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {customItems.map((item) => (
                  <tr key={item.id} className="border-b">
                    <td className="p-2">{item.name || "—"}</td>
                    <td className="p-2">{item.quantity}</td>
                    <td className="p-2 text-right">{formatCurrency(item.rate)}</td>
                    <td className="p-2 text-right">{formatCurrency(item.quantity * item.rate)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateCustomItemsTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Grand Total */}
        <div className="pt-4 mt-8 border-t-2">
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold text-gray-900">Grand Total:</span>
            <span className="text-3xl font-bold text-primary">
              {formatCurrency(calculateGrandTotal())}
            </span>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark"
          >
            Print Bill
          </button>
        </div>
      </div>
    );
  };

  const renderPreInvoiceView = () => {
    if (!billingData) return null;
    const editableTotal = calculateEditableTotal();
    const hasItems =
      billingData.pharmacy.length > 0 ||
      billingData.labOrders.length > 0 ||
      billingData.services.length > 0 ||
      (billingData.roomCharges && billingData.roomCharges.length > 0);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border">
          <div>
            <div className="text-sm text-gray-600">Unallocated Advance</div>
            <div className="mt-1 text-xl font-bold text-green-700">
              {formatCurrency(getAdvanceBalance())}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 p-4 bg-yellow-50 rounded-lg border sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm text-gray-600">No bill generated for this visit</div>
            <div className="text-lg font-semibold text-gray-900">
              Collect advance or generate bill
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setIsAdvanceModalOpen(true)}
              className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              Collect Advance
            </button>
            <button
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || !hasItems}
              className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
            >
              {isGeneratingInvoice ? "Generating..." : "Generate Bill"}
            </button>
          </div>
        </div>

        <div className="border rounded-lg flex flex-col max-h-[60vh]">
          <div className="p-4 bg-gray-50 border-b shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">Edit Rates</h3>
          </div>
          <div className="overflow-y-auto flex-1 p-4 space-y-3 min-h-0">
            {billingData.pharmacy.map((sale) => {
              const key = `pharmacy-${sale.id}`;
              return (
                <div key={key} className="flex gap-4 justify-between items-center p-3 rounded-lg border">
                  <div>
                    <div className="font-medium">Pharmacy Sale #{sale.saleNumber}</div>
                    <div className="text-sm text-gray-500">{formatDate(sale.saleDate)}</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, sale.netAmount || 0)}
                    onChange={(e) => {
                      setRatesSaved(false);
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }));
                    }}
                    className="px-3 py-2 w-32 text-right rounded-lg border"
                  />
                </div>
              );
            })}

            {billingData.labOrders.map((order) => {
              const key = `lab-${order.id}`;
              return (
                <div key={key} className="flex gap-4 justify-between items-center p-3 rounded-lg border">
                  <div>
                    <div className="font-medium">Lab Order #{order.orderNumber}</div>
                    <div className="text-sm text-gray-500">{formatDate(order.orderDate)}</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, order.totalAmount || 0)}
                    onChange={(e) => {
                      setRatesSaved(false);
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }));
                    }}
                    className="px-3 py-2 w-32 text-right rounded-lg border"
                  />
                </div>
              );
            })}

            {billingData.services.map((service) => {
              const key = `service-${service.id}`;
              return (
                <div key={key} className="flex gap-4 justify-between items-center p-3 rounded-lg border">
                  <div>
                    <div className="font-medium">{service.name}</div>
                    <div className="text-sm text-gray-500">
                      Quantity: {service.quantity}
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, service.total || 0)}
                    onChange={(e) => {
                      setRatesSaved(false);
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }));
                    }}
                    className="px-3 py-2 w-32 text-right rounded-lg border"
                  />
                </div>
              );
            })}

            {billingData.roomCharges?.map((room, idx) => {
              const key = `room-${room.roomId}-${idx}`;
              const isExcluded = excludedRooms.has(key);
              return (
                <div key={key} className={`flex gap-4 justify-between items-center p-3 rounded-lg border ${isExcluded ? "opacity-50 bg-gray-50" : ""}`}>
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      id={`include-room-${key}`}
                      checked={!isExcluded}
                      onChange={(e) => {
                        setRatesSaved(false);
                        setExcludedRooms((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.delete(key);
                          else next.add(key);
                          return next;
                        });
                      }}
                      className="mt-1 h-4 w-4 cursor-pointer accent-primary"
                    />
                    <label htmlFor={`include-room-${key}`} className="cursor-pointer">
                      <div className="font-medium">
                        {room.roomName} ({room.roomType})
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                      </div>
                      {isExcluded && (
                        <div className="text-xs text-orange-500 mt-0.5">Not included in bill</div>
                      )}
                    </label>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, room.charge || 0)}
                    disabled={isExcluded}
                    onChange={(e) => {
                      setRatesSaved(false);
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }));
                    }}
                    className="px-3 py-2 w-32 text-right rounded-lg border disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              );
            })}

            <div className="p-3 space-y-3 rounded-lg border">
              <div className="flex justify-between items-center">
                <div className="font-medium text-gray-900">Custom Items</div>
                <button
                  onClick={addCustomItem}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Add Item
                </button>
              </div>

              {customItems.length === 0 && (
                <div className="text-sm text-gray-500">No custom items added.</div>
              )}

              {customItems.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Item name"
                    value={item.name}
                    onChange={(e) => updateCustomItem(item.id, { name: e.target.value })}
                    className="col-span-6 px-3 py-2 rounded-lg border"
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.quantity}
                    onChange={(e) =>
                      updateCustomItem(item.id, { quantity: parseInt(e.target.value, 10) || 0 })
                    }
                    className="col-span-2 px-3 py-2 text-right rounded-lg border"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.rate}
                    onChange={(e) =>
                      updateCustomItem(item.id, { rate: parseFloat(e.target.value) || 0 })
                    }
                    className="col-span-3 px-3 py-2 text-right rounded-lg border"
                  />
                  <button
                    onClick={() => removeCustomItem(item.id)}
                    className="col-span-1 text-sm text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {!hasItems && (
              <div className="text-sm text-gray-500">No bill items available.</div>
            )}
          </div>
          <div className="flex justify-between items-center p-4 bg-gray-50 border-t shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveRates}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-primary hover:bg-hover"
              >
                Save Rates
              </button>
              {ratesSaved && (
                <span className="text-sm text-green-600 font-medium">
                  ✓ Rates saved — click Generate Bill to create invoice
                </span>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 mb-0.5">Bill Total</div>
              <span className="text-xl font-bold text-primary">
                {formatCurrency(editableTotal)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentsView = () => {
    if (!hasInvoice) {
      return renderPreInvoiceView();
    }
    const outstanding = calculateOutstanding();
    const unallocatedBalance = getAdvanceBalance();
    const selectedTotal = getSelectedItemsTotal();
    const invoiceStatus = invoiceData?.invoice?.status;
    const isInvoicePaid = invoiceStatus === "paid" || outstanding <= 0;

    return (
      <div className="space-y-6">
        {/* Balance Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border">
            <div className="text-sm text-gray-600">Current Balance</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">
              {formatCurrency(calculateGrandTotal())}
            </div>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg border">
            <div className="text-sm text-gray-600">Outstanding</div>
            <div className="mt-1 text-2xl font-bold text-yellow-700">
              {isInvoicePaid ? "Paid" : formatCurrency(outstanding)}
            </div>
          </div>
        </div>

        {isInvoicePaid && (
          <div className="p-4 bg-green-50 rounded-lg border">
            <div className="text-sm text-gray-600">Invoice Status</div>
            <div className="mt-1 text-lg font-semibold text-green-700">
              Paid in full
            </div>
          </div>
        )}

        {/* Unallocated Balance */}
        <div className="p-4 bg-green-50 rounded-lg border">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm text-gray-600">Unallocated Balance</div>
              <div className="mt-1 text-xl font-bold text-green-700">
                {formatCurrency(unallocatedBalance)}
              </div>
            </div>

          </div>
        </div>

        {!isInvoicePaid && (
          <div className="border rounded-lg flex flex-col max-h-[60vh]">
            <div className="p-4 bg-gray-50 border-b shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Select Items to Pay</h3>
              <p className="mt-1 text-xs text-gray-400">
                Selecting items here is only for calculating the payment amount. Individual items are not marked as paid separately — all payments are applied to the single visit invoice.
              </p>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-4 min-h-0">
              {billingData?.pharmacy.map((sale) => (
                <label
                  key={`pharmacy-${sale.id}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`pharmacy-${sale.id}`)}
                      onChange={() => handleItemSelection(`pharmacy-${sale.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Pharmacy Sale #{sale.saleNumber}</div>
                      <div className="text-sm text-gray-500">{formatDate(sale.saleDate)}</div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(sale.netAmount)}</span>
                </label>
              ))}

              {billingData?.labOrders.map((order) => (
                <label
                  key={`lab-${order.id}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`lab-${order.id}`)}
                      onChange={() => handleItemSelection(`lab-${order.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Lab Order #{order.orderNumber}</div>
                      <div className="text-sm text-gray-500">{formatDate(order.orderDate)}</div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(order.totalAmount)}</span>
                </label>
              ))}

              {billingData?.services.map((service) => (
                <label
                  key={`service-${service.id}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`service-${service.id}`)}
                      onChange={() => handleItemSelection(`service-${service.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">{service.name}</div>
                      <div className="text-sm text-gray-500">
                        Quantity: {service.quantity}
                      </div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(service.total)}</span>
                </label>
              ))}

              {billingData?.roomCharges?.map((room, idx) => (
                <label
                  key={`room-${room.roomId}-${idx}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`room-${room.roomId}-${idx}`)}
                      onChange={() => handleItemSelection(`room-${room.roomId}-${idx}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">
                        {room.roomName} ({room.roomType})
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                      </div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(room.charge)}</span>
                </label>
              ))}
            </div>

            {selectedItems.size > 0 && (
              <div className="p-4 bg-gray-50 border-t shrink-0">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-gray-900">Selected Total:</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(selectedTotal)}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => openPaymentModal("selected")}
                    disabled={isProcessingPayment}
                    className="flex-1 px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
                  >
                    {isProcessingPayment ? "Processing..." : "Make Payment"}
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Implement new payment modal
                      showNotification("info", "New Payment", "Feature coming soon", "topRight");
                    }}
                    className="flex-1 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    New Payment
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment Receipts — one row per unique PatientTransaction */}
        {invoiceData?.invoice?.paymentAllocations && invoiceData.invoice.paymentAllocations.length > 0 && (() => {
          // Deduplicate by patientTransaction.id — one receipt per payment, even if allocated to multiple lines
          const seen = new Set<number>();
          const uniqueAllocations: any[] = [];
          for (const alloc of invoiceData.invoice.paymentAllocations) {
            const txId = alloc.patientTransaction?.id;
            if (txId != null && !seen.has(txId)) {
              seen.add(txId);
              uniqueAllocations.push(alloc);
            }
          }
          return (
            <div className="rounded-lg border">
              <div className="p-4 bg-gray-50 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Payments Received</h3>
              </div>
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="p-2 text-left font-medium">Date</th>
                      <th className="p-2 text-left font-medium">Receipt #</th>
                      <th className="p-2 text-left font-medium">Amount</th>
                      <th className="p-2 text-left font-medium">Mode</th>
                      <th className="p-2 text-left font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueAllocations.map((alloc: any) => (
                      <tr key={alloc.patientTransaction?.id} className="border-b last:border-b-0">
                        <td className="p-2 text-gray-700">
                          {alloc.patientTransaction?.paymentDate
                            ? format(new Date(alloc.patientTransaction.paymentDate), "dd MMM yyyy")
                            : "-"}
                        </td>
                        <td className="p-2 font-mono text-xs text-gray-700">
                          {alloc.patientTransaction?.receiptNumber || "-"}
                        </td>
                        <td className="p-2 font-semibold text-gray-900">
                          {formatCurrency(alloc.patientTransaction?.amount)}
                        </td>
                        <td className="p-2 text-gray-600 capitalize">
                          {alloc.patientTransaction?.paymentMode || "-"}
                        </td>
                        <td className="p-2">
                          <button
                            onClick={() => handlePrintReceipt(alloc.id)}
                            className="px-2 py-1 text-xs text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-50"
                          >
                            Print Receipt
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Make Payment (Outstanding) */}
        {!isInvoicePaid && (
          <div className="flex justify-end">
            <button
              onClick={() => openPaymentModal("outstanding")}
              disabled={isProcessingPayment}
              className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
            >
              Make Payment
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleSaveInsurance = async () => {
    // If a breakdown is present, derive claimed amount from it; otherwise fall back to the manual field
    const effectiveClaimedAmount = claimBreakdown.length > 0 ? breakdownTotal : parseFloat(insuranceForm.claimedAmount);
    const parsedClaimedAmount = effectiveClaimedAmount;
    if (!(Number.isFinite(parsedClaimedAmount) && parsedClaimedAmount > 0)) {
      showNotification("error", "Validation error", "Claimed amount must be greater than 0.", "topRight");
      return;
    }

    const today = format(new Date(), "yyyy-MM-dd");
    const effectiveStatus = (insuranceForm.status || "pending").trim() || "pending";
    const effectiveClaimDate = (insuranceForm.claimDate || today).trim() || today;
    const normalizedStatus = String(effectiveStatus ?? "").toLowerCase();
    const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
    const parsedApprovedAmount = insuranceForm.approvedAmount ? parseFloat(insuranceForm.approvedAmount) : NaN;
    if (requiresApprovedAmount && !(Number.isFinite(parsedApprovedAmount) && parsedApprovedAmount > 0)) {
      showNotification("error", "Validation error", "Approved amount must be greater than 0 for Approved / Partially Approved claims.", "topRight");
      return;
    }
    const effectiveClaimed = claimBreakdown.length > 0 ? breakdownTotal : parsedClaimedAmount;
    if (requiresApprovedAmount && Number.isFinite(parsedApprovedAmount) && parsedApprovedAmount > effectiveClaimed) {
      showNotification("error", "Validation error", "Approved amount cannot be greater than claimed amount.", "topRight");
      return;
    }
    const effectiveApprovalDate = (insuranceForm.approvalDate || "").trim();
    if (requiresApprovedAmount && !effectiveApprovalDate) {
      showNotification("error", "Validation error", "Approval date is required for Approved / Partially Approved claims.", "topRight");
      return;
    }

    setIsSavingInsurance(true);
    const payload = {
      insuranceProviderId: insuranceForm.insuranceProviderId ?? undefined,
      policyNumber: insuranceForm.policyNumber.trim() || undefined,
      coverageType: insuranceForm.coverageType,
      claimedAmount: parsedClaimedAmount,
      approvedAmount: insuranceForm.approvedAmount ? parseFloat(insuranceForm.approvedAmount) : undefined,
      rejectedAmount: insuranceForm.rejectedAmount ? parseFloat(insuranceForm.rejectedAmount) : undefined,
      status: effectiveStatus,
      claimDate: effectiveClaimDate,
      approvalDate: effectiveApprovalDate || undefined,
      remarks: insuranceForm.remarks.trim() || undefined,
      claimBreakdown: claimBreakdown.length > 0
        ? claimBreakdown.map((line) => ({
            serviceCategoryCode: line.serviceCategoryCode,
            providerLabel: line.providerLabel,
            amount: parseFloat(line.amount) || 0,
          }))
        : undefined,
    };
    try {
      if (editingClaimId != null) {
        await updateVisitInsuranceClaim(visitId, editingClaimId, payload);
        showNotification("success", "Insurance claim updated", "", "topRight");
      } else {
        await saveVisitInsuranceClaim(visitId, payload);
        showNotification("success", "Insurance claim added", "", "topRight");
      }
      setEditingClaimId(null);
      setInsuranceViewMode("list");
      fetchInsuranceData();
    } catch (error: any) {
      showNotification("error", "Error", error?.response?.data?.message || "Failed to save insurance claim", "topRight");
    } finally {
      setIsSavingInsurance(false);
    }
  };

  const formatClaimStatus = (status: string) => {
    if (!status) return "—";
    return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const pharmacyTotal = billingData
    ? billingData.pharmacy.reduce((s, sale) => s + (sale.netAmount ?? 0), 0)
    : 0;

  const fetchClaimTemplatesForProvider = async (providerId: number, breakdown?: ClaimBreakdownLine[]) => {
    setIsLoadingTemplates(true);
    try {
      const res: any = await getClaimTemplates(providerId);
      const templates: any[] = res?.data ?? res ?? [];
      const visible = templates.filter((t: any) => (t.isVisible ?? t.is_visible) !== false);
      setClaimTemplates(visible);

      if (breakdown && breakdown.length > 0) {
        setClaimBreakdown(breakdown);
      } else {
        // Auto-populate using componentTotals (from billing API) and billingSourceType
        const componentTotals: Record<number, number> =
          (billingData as any)?.componentTotals ?? {};

        setClaimBreakdown(
          visible.map((t: any) => {
            const sourceType = t.billingSourceType ?? t.billing_source_type ?? "component";
            const componentId = t.serviceComponentId ?? t.service_component_id ?? null;
            let amount = 0;
            if (sourceType === "pharmacy") {
              amount = pharmacyTotal;
            } else if (sourceType === "component" && componentId) {
              amount = componentTotals[componentId] ?? 0;
            }
            return {
              serviceCategoryCode: String(componentId ?? sourceType),
              providerLabel: t.providerLabel ?? t.provider_label,
              amount: amount.toFixed(2),
            };
          })
        );
      }
    } catch (e) {
      console.error("Failed to load claim templates", e);
      setClaimTemplates([]);
      setClaimBreakdown([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const breakdownTotal = claimBreakdown.reduce(
    (sum, line) => sum + (parseFloat(line.amount) || 0),
    0
  );

  const openAddInsuranceClaimForm = () => {
    setEditingClaimId(null);
    setClaimTemplates([]);
    setClaimBreakdown([]);
    const today = format(new Date(), "yyyy-MM-dd");
    const baseDefaultForm: InsuranceClaimForm = {
      insuranceProviderId: null,
      policyNumber: "",
      coverageType: "reimbursement" as const,
      claimedAmount: "",
      approvedAmount: "",
      rejectedAmount: "",
      status: "pending",
      claimDate: today,
      approvalDate: "",
      remarks: "",
    };
    const defaultForm: InsuranceClaimForm = {
      ...baseDefaultForm,
      ...(defaultInsuranceFormFromAdmission ?? {}),
      status: (defaultInsuranceFormFromAdmission?.status || baseDefaultForm.status) as string,
      claimDate: (defaultInsuranceFormFromAdmission?.claimDate || baseDefaultForm.claimDate) as string,
    };
    setInsuranceForm(defaultForm);
    if (defaultForm.insuranceProviderId) {
      fetchClaimTemplatesForProvider(defaultForm.insuranceProviderId);
    }
    setInsuranceViewMode("form");
  };

  const openEditInsuranceClaimForm = (claim: any) => {
    const id = claim?.id ?? claim?.claim_id;
    if (id != null) setEditingClaimId(Number(id));
    const providerId = claim?.insuranceProviderId ?? claim?.insurance_provider_id ?? null;
    const savedBreakdown: ClaimBreakdownLine[] = (claim?.claimBreakdown ?? claim?.claim_breakdown ?? []).map((line: any) => ({
      serviceCategoryCode: line.serviceCategoryCode ?? line.service_category_code,
      providerLabel: line.providerLabel ?? line.provider_label,
      amount: String(line.amount ?? "0"),
    }));
    setInsuranceForm({
      insuranceProviderId: providerId ?? null,
      policyNumber: claim?.policyNumber ?? claim?.policy_number ?? "",
      coverageType: (claim?.coverageType ?? claim?.coverage_type ?? "reimbursement") as "reimbursement" | "cashless",
      claimedAmount: claim != null && (claim.claimedAmount != null || claim.claimed_amount != null) ? String(claim.claimedAmount ?? claim.claimed_amount ?? "") : "",
      approvedAmount: claim != null && (claim.approvedAmount != null || claim.approved_amount != null) ? String(claim.approvedAmount ?? claim.approved_amount ?? "") : "",
      rejectedAmount: claim != null && (claim.rejectedAmount != null || claim.rejected_amount != null) ? String(claim.rejectedAmount ?? claim.rejected_amount ?? "") : "",
      status: claim?.status ?? "",
      claimDate: claim?.claimDate ?? claim?.claim_date ? format(new Date(claim.claim_date ?? claim.claimDate), "yyyy-MM-dd") : "",
      approvalDate: claim?.approvalDate ?? claim?.approval_date ? format(new Date(claim.approval_date ?? claim.approvalDate), "yyyy-MM-dd") : "",
      remarks: claim?.remarks ?? "",
    });
    if (providerId) {
      fetchClaimTemplatesForProvider(providerId, savedBreakdown.length > 0 ? savedBreakdown : undefined);
    } else {
      setClaimTemplates([]);
      setClaimBreakdown(savedBreakdown);
    }
    setInsuranceViewMode("form");
  };

  const renderInsuranceListView = () => {
    const claims = insuranceClaimsList ?? [];
    const blockingStatuses = new Set(["pending", "submitted", "partial"]);
    const hasBlockingClaim = claims.some((c: any) =>
      blockingStatuses.has(String(c?.status ?? "").toLowerCase())
    );
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Insurance Claims</h2>
          <button
            type="button"
            onClick={openAddInsuranceClaimForm}
            disabled={hasBlockingClaim}
            title={hasBlockingClaim ? "You cannot add a new claim while any claim is Pending / Submitted / Partial." : undefined}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed bg-primary hover:bg-primary-dark"
          >
            Add insurance claim
          </button>
        </div>
        {hasBlockingClaim && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            You cannot add a new claim while any claim is Pending / Submitted / Partial. Edit or resolve those claim(s) first.
          </p>
        )}
        {claims.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-500">
            <p className="mb-4">No insurance claim recorded for this visit yet.</p>
            <button
              type="button"
              onClick={openAddInsuranceClaimForm}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark"
            >
              Add insurance claim
            </button>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Insurance Provider</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policy Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coverage Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Claimed (₹)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Approved (₹)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected (₹)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Claim Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approval Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {claims.map((claim: any) => (
                  <tr key={claim.id ?? claim.claim_id ?? Math.random()}>
                    <td className="px-4 py-3 text-sm text-left whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openEditInsuranceClaimForm(claim)}
                        className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 hover:border-primary-dark"
                      >
                        Edit
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{formatClaimStatus(claim.status ?? "")}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {claim.insuranceProvider?.name ?? claim.insurance_provider?.name ?? claim.insuranceProviderName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{claim.policyNumber ?? claim.policy_number ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 capitalize">{claim.coverageType ?? claim.coverage_type ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(claim.claimedAmount ?? claim.claimed_amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(claim.approvedAmount ?? claim.approved_amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(claim.rejectedAmount ?? claim.rejected_amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {claim.claimDate ?? claim.claim_date ? format(new Date(claim.claim_date ?? claim.claimDate), "dd MMM yyyy") : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {claim.approvalDate ?? claim.approval_date ? format(new Date(claim.approval_date ?? claim.approvalDate), "dd MMM yyyy") : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openEditInsuranceClaimForm(claim)}
                        className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 hover:border-primary-dark"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderInsuranceFormView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Insurance & Claim</h2>
        <button
          type="button"
          onClick={() => { setEditingClaimId(null); setInsuranceViewMode("list"); }}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back to list
        </button>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Insurance Provider</label>
            <select
              value={insuranceForm.insuranceProviderId ?? ""}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null;
                setInsuranceForm({ ...insuranceForm, insuranceProviderId: id });
                setClaimTemplates([]);
                setClaimBreakdown([]);
                if (id) fetchClaimTemplatesForProvider(id);
              }}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            >
              <option value="">Select provider</option>
              {insuranceProviders.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Policy Number</label>
            <input
              type="text"
              value={insuranceForm.policyNumber}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, policyNumber: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
              placeholder="Policy number"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Coverage Type</label>
            <div className="flex gap-6 mt-2">
              <label className="inline-flex gap-2 items-center cursor-pointer">
                <input
                  type="radio"
                  name="coverageType"
                  checked={insuranceForm.coverageType === "reimbursement"}
                  onChange={() => setInsuranceForm({ ...insuranceForm, coverageType: "reimbursement" })}
                  className="border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Reimbursement</span>
              </label>
              <label className="inline-flex gap-2 items-center cursor-pointer">
                <input
                  type="radio"
                  name="coverageType"
                  checked={insuranceForm.coverageType === "cashless"}
                  onChange={() => setInsuranceForm({ ...insuranceForm, coverageType: "cashless" })}
                  className="border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Cashless</span>
              </label>
            </div>
          </div>
          {/* Claimed amount: auto-computed from breakdown or manual if no provider selected */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Claimed Amount (₹)</label>
            {claimBreakdown.length > 0 ? (
              <div className="px-3 py-2 w-full rounded-md border border-gray-200 bg-gray-50 text-gray-900 font-semibold">
                {formatCurrency(breakdownTotal)}
                <span className="ml-2 text-xs font-normal text-gray-500">auto-computed from breakdown</span>
              </div>
            ) : (
              <>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={insuranceForm.claimedAmount}
                  onChange={(e) => setInsuranceForm({ ...insuranceForm, claimedAmount: e.target.value })}
                  className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
                  placeholder="0.00"
                />
                {!(Number.isFinite(Number(insuranceForm.claimedAmount)) && Number(insuranceForm.claimedAmount) > 0) && (
                  <p className="mt-1 text-xs text-red-600">Claimed amount must be greater than 0.</p>
                )}
              </>
            )}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Approved Amount (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={insuranceForm.approvedAmount}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, approvedAmount: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
              placeholder="0.00"
            />
            {(() => {
              const normalizedStatus = String(insuranceForm.status ?? "").toLowerCase();
              const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
              if (!requiresApprovedAmount) return null;
              const approved = Number(insuranceForm.approvedAmount);
              const claimed = Number(insuranceForm.claimedAmount);
              if (!(Number.isFinite(approved) && approved > 0)) {
                return <p className="mt-1 text-xs text-red-600">Approved amount must be greater than 0 for Approved / Partially Approved claims.</p>;
              }
              if (Number.isFinite(claimed) && approved > claimed) {
                return <p className="mt-1 text-xs text-red-600">Approved amount cannot be greater than claimed amount.</p>;
              }
              return null;
            })()}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Rejected Amount (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={insuranceForm.rejectedAmount}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, rejectedAmount: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Status</label>
            <select
              value={insuranceForm.status}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, status: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            >
              <option value="">Select status</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="partially_approved">Partially Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Claim Date</label>
            <input
              type="date"
              value={insuranceForm.claimDate}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, claimDate: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Approval Date</label>
            <input
              type="date"
              value={insuranceForm.approvalDate}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, approvalDate: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            />
            {(() => {
              const normalizedStatus = String(insuranceForm.status ?? "").toLowerCase();
              const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
              if (!requiresApprovedAmount) return null;
              return !String(insuranceForm.approvalDate ?? "").trim() ? (
                <p className="mt-1 text-xs text-red-600">Approval date is required for Approved / Partially Approved claims.</p>
              ) : null;
            })()}
          </div>
        </div>
        {/* Claim breakdown table */}
        {insuranceForm.insuranceProviderId && (
          <div className="md:col-span-2 border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-800">
                Claim Breakdown by Provider Template
              </h3>
              {isLoadingTemplates && (
                <span className="text-xs text-gray-500 animate-pulse">Loading templates…</span>
              )}
            </div>

            {!isLoadingTemplates && claimBreakdown.length === 0 && (
              <p className="px-4 py-3 text-sm text-gray-500">
                No claim templates configured for this provider. Enter claimed amount manually above.
              </p>
            )}

            {claimBreakdown.length > 0 && (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 w-1/2">
                      Provider Label
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 w-1/4">
                      Category
                    </th>
                    <th className="px-4 py-2 text-right font-medium text-gray-600 w-1/4">
                      Amount (₹)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {claimBreakdown.map((line, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800">
                        {line.providerLabel}
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs">
                        {line.serviceCategoryCode}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.amount}
                          onChange={(e) => {
                            const updated = [...claimBreakdown];
                            updated[idx] = { ...updated[idx], amount: e.target.value };
                            setClaimBreakdown(updated);
                          }}
                          className="w-28 px-2 py-1 text-right rounded border border-gray-300 focus:ring-primary focus:border-primary text-sm"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t">
                  <tr>
                    <td colSpan={2} className="px-4 py-2 text-right font-semibold text-gray-700">
                      Total Claimed
                    </td>
                    <td className="px-4 py-2 text-right font-bold text-gray-900">
                      {formatCurrency(breakdownTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}

        <div className="md:col-span-2">
          <label className="block mb-1 text-sm font-medium text-gray-700">Remarks</label>
          <textarea
            value={insuranceForm.remarks}
            onChange={(e) => setInsuranceForm({ ...insuranceForm, remarks: e.target.value })}
            rows={3}
            className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            placeholder="Remarks"
          />
        </div>
        <div className="md:col-span-2 flex justify-end pt-4">
          <button
            type="button"
            onClick={handleSaveInsurance}
            disabled={
              isSavingInsurance ||
              (claimBreakdown.length > 0 ? !(breakdownTotal > 0) : !(Number.isFinite(Number(insuranceForm.claimedAmount)) && Number(insuranceForm.claimedAmount) > 0)) ||
              ((() => {
                const normalizedStatus = String(insuranceForm.status ?? "").toLowerCase();
                const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
                if (!requiresApprovedAmount) return false;
                const approved = Number(insuranceForm.approvedAmount);
                const effectiveClaimed = claimBreakdown.length > 0 ? breakdownTotal : Number(insuranceForm.claimedAmount);
                const approvedAmountInvalid = !(Number.isFinite(approved) && approved > 0) || (Number.isFinite(effectiveClaimed) && approved > effectiveClaimed);
                const approvalDateMissing = !String(insuranceForm.approvalDate ?? "").trim();
                return approvedAmountInvalid || approvalDateMissing;
              })())
            }
            className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
          >
            {isSavingInsurance ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
  );

  const renderInsuranceView = () => {
    if (isLoadingInsurance) return <LoaderComponent />;
    return insuranceViewMode === "list" ? renderInsuranceListView() : renderInsuranceFormView();
  };

  if (!isOpen) return null;

  return (
    <div className="overflow-hidden fixed inset-0 z-50">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      ></div>

      <div className="flex justify-center items-center p-4 min-h-screen">
        <div className="inline-block w-full max-w-7xl h-[90vh] overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex justify-between p-4 text-white bg-primary">
            <span className="text-lg font-bold">IPD Visit Billing</span>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Main Content */}
          <div className="flex h-[calc(90vh-64px)]">
            {/* Left Navigation - 25% */}
            <div className="overflow-y-auto w-1/4 bg-gray-50 border-r">
              <nav className="p-4 space-y-2">
                <button
                  onClick={() => setActiveNav("summary")}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "summary"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setActiveNav("billItems")}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "billItems"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Bill Items
                </button>
                <button
                  onClick={() => {
                    setActiveNav("payments");
                    fetchPaymentData();
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "payments"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Payments & Allocations
                </button>
                <button
                  onClick={() => {
                    setActiveNav("insurance");
                    setInsuranceViewMode("list");
                    fetchInsuranceData();
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "insurance"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Insurance
                </button>
              </nav>
            </div>

            {/* Right Content - 75% */}
            <div className="overflow-y-auto p-6 w-3/4">
              {activeNav === "insurance" ? (
                isLoadingInsurance ? <LoaderComponent /> : renderInsuranceView()
              ) : isLoading ? (
                <LoaderComponent />
              ) : (
                <>
                  {activeNav === "summary" && renderSummaryView()}
                  {activeNav === "billItems" && renderBillItemsView()}
                  {activeNav === "payments" && renderPaymentsView()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isAdvanceModalOpen && (
        <PaymentModal
          isOpen={isAdvanceModalOpen}
          onClose={() => setIsAdvanceModalOpen(false)}
          total={0}
          discount={0}
          patientId={patientId}
          sourceType="visit"
          onSave={handleAdvanceSave}
          onPaymentSuccess={() => setIsAdvanceModalOpen(false)}
        />
      )}

      {isReceiptSlipOpen && (
        <ReceiptSlipModal
          isOpen={isReceiptSlipOpen}
          onClose={() => { setIsReceiptSlipOpen(false); setReceiptSlipData(null); }}
          receiptData={receiptSlipData}
        />
      )}

      {isPaymentModalOpen && (() => {
        const cashlessClaim = (insuranceClaimsList ?? []).find(
          (c: any) =>
            (c.coverageType ?? c.coverage_type) === "cashless" &&
            (c.status === "approved" || c.status === "partially_approved")
        );
        const cashlessApproved = cashlessClaim
          ? parseFloat(String(cashlessClaim.approvedAmount ?? cashlessClaim.approved_amount ?? 0)) || 0
          : 0;
        return (
          <PaymentModal
            isOpen={isPaymentModalOpen}
            onClose={() => setIsPaymentModalOpen(false)}
            total={
              paymentContext === "selected"
                ? getSelectedItemsTotal()
                : calculateOutstanding()
            }
            discount={0}
            patientId={patientId}
            sourceType="visit"
            advanceBalance={getAdvanceBalance()}
            cashlessInsuranceAmount={cashlessApproved > 0 ? cashlessApproved : undefined}
            onSave={handleInvoicePaymentSave}
            onPaymentSuccess={() => setIsPaymentModalOpen(false)}
          />
        );
      })()}
    </div>
  );
};

export default IPDBillingModal;
